import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { logoutUser } from '../../services/auth';
import Logo from '../common/Logo';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isAuthenticated, clearAuth } = useAuthStore();

  const handleLogout = async () => {
    await logoutUser();
    clearAuth();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  // Don't show header on welcome and auth pages
  if (location.pathname === '/' || location.pathname === '/auth') return null;

  // Check if profile is completed
  const isProfileComplete = profile?.profileCompleted;

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div 
            onClick={() => handleNavigation(isProfileComplete ? '/dashboard' : '/profile-step-1')}
            className="flex items-center cursor-pointer"
          >
            <Logo size="md" className="mr-3" />
            <span className="text-xl font-bold text-gray-900">CreditPesa Loans to Mpesa</span>
          </div>

          {/* Desktop Navigation - Only show if profile is complete */}
          {isAuthenticated && isProfileComplete && (
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => handleNavigation('/dashboard')}
                className={`text-sm font-medium transition-colors ${
                  isActive('/dashboard') 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => handleNavigation('/service-fee-selection')}
                className={`text-sm font-medium transition-colors ${
                  isActive('/service-fee-selection') 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Apply Loan
              </button>
              <button
                onClick={() => handleNavigation('/profile')}
                className={`text-sm font-medium transition-colors ${
                  isActive('/profile') 
                    ? 'text-primary-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Profile
              </button>
            </nav>
          )}

          {/* User Menu */}
          <div className="flex items-center">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-900 transition-colors"
                >
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-2">
                    <span className="text-primary-600 font-medium">
                      {profile?.fullName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block">
                    {profile?.fullName || user?.email?.split('@')[0] || 'User'}
                  </span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
                    {/* Mobile Navigation - Only show if profile complete */}
                    {isProfileComplete && (
                      <>
                        <button
                          onClick={() => handleNavigation('/dashboard')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 md:hidden"
                        >
                          Dashboard
                        </button>
                        <button
                          onClick={() => handleNavigation('/service-fee-selection')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 md:hidden"
                        >
                          Apply Loan
                        </button>
                        <button
                          onClick={() => handleNavigation('/profile')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Profile Settings
                        </button>
                        <hr className="my-1" />
                      </>
                    )}
                    
                    {/* Profile completion status */}
                    {!isProfileComplete && (
                      <>
                        <div className="px-4 py-2 text-xs text-gray-500 bg-yellow-50">
                          Complete your profile to access all features
                        </div>
                        <hr className="my-1" />
                      </>
                    )}
                    
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleNavigation('/auth')}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;